import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import * as XLSX from 'xlsx';
import { FormBuilder, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { VendorResultsDomain, VendorTakeActionDomain, AFVendorResponseDomain } from '../model/af.model';
import { AuthService } from '../service/auth.service';
import { VendorService } from '../service/vendor.service';
import * as bcrypt from 'bcryptjs';
import { CustomValidatorsService } from '../service/custom-validators.service';
import { VendorSearchResultsTakeActionComponent } from '../vendor-search-results-take-action/vendor-search-results-take-action.component';
import { MatDialog } from '@angular/material/dialog';

interface VendorSearchResultsFormModel {
  caseNumber: FormControl<string>,
  referenceNumber: FormControl<string>,
  applicantFirstName: FormControl<string>,
  applicantLastName: FormControl<string>,
  requesterFirstName: FormControl<string>,
  requesterLastName: FormControl<string>,
  ssn: FormControl<string>,
  companyCode: FormControl<string>,
  vendorName: FormControl<string>,
  caseOrderStartDate: FormControl<string>,
  caseOrderEndDate: FormControl<string>
}

@Component({
  selector: 'app-vendor-search-results',
  templateUrl: './vendor-search-results.component.html',
  styleUrls: ['./vendor-search-results.component.scss'],
  standalone: false
})

export class VendorSearchResultsComponent implements OnInit {

  vendorSearchResultsForm: UntypedFormGroup;
  maxDate = new Date();
  minDate = new Date(1900, 0, 1);
  displayResult: boolean = false;
  dataSource: MatTableDataSource<VendorResultsDomain>;
  displayedColumns: string[] = ['caseNumber', 'referenceNumber', 'applicantName', 'requesterName', 'comp', 'appointmentDetails', 'caseStatus', 'statusDate'];
  config: MatSnackBarConfig;
  @ViewChild('TABLE') table: ElementRef;

  private sort: MatSort;
  private paginator: MatPaginator;
  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.dataSource.sort = this.sort;
  }
  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.dataSource.paginator = this.paginator;
  }

  @ViewChild("vendorSearchElement") vendorSearchElement: ElementRef;

  regexCaseNumber: RegExp = /^$|^\d{6}-\d{12}$/;
  regexName: RegExp = /^[A-Za-z\u00C0-\u017F\-',. ]+$/;
  regexStr: RegExp = /^[a-zA-Z0-9\s-&().,'"/:;]*$/;

  constructor(private readonly fb: FormBuilder, private readonly _snackBar: MatSnackBar,
    private readonly userService: AuthService, private readonly vendorService: VendorService,
    private readonly customValidator: CustomValidatorsService, private readonly dialog: MatDialog) { }

  ngOnInit(): void {
    this.createFormGroup();
    this.dataSource = new MatTableDataSource<VendorResultsDomain>([]);

    this.config = new MatSnackBarConfig();
    let horizontalPosition: MatSnackBarHorizontalPosition = 'center';
    let verticalPosition: MatSnackBarVerticalPosition = 'top';
    this.config.verticalPosition = verticalPosition;
    this.config.horizontalPosition = horizontalPosition;
    this.config.duration = 5000;
    this.config.panelClass = 'submit-error';

    this.vendorService.getFocusEmitter().subscribe({
      next: (data: any) => {
        if (data === 'vendorSearchRequest') {
          this.vendorFormFocus();
        }
      }
    });
  };

  exportAsExcel() {
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(this.table.nativeElement);//converts a DOM TABLE element to a worksheet
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    /* save to file */
    XLSX.writeFile(wb, 'Screening Results.xlsx');

  }
  vendorFormFocus() {
    setTimeout(() => {
      this.vendorSearchElement.nativeElement.focus();
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  get caseNumber() { return this.vendorSearchResultsForm.get('caseNumber') };
  get referenceNumber() { return this.vendorSearchResultsForm.get('referenceNumber') };
  get applicantFirstName() { return this.vendorSearchResultsForm.get('applicantFirstName') };
  get applicantLastName() { return this.vendorSearchResultsForm.get('applicantLastName') };
  get requesterFirstName() { return this.vendorSearchResultsForm.get('requesterFirstName') };
  get requesterLastName() { return this.vendorSearchResultsForm.get('requesterLastName') };
  get ssn() { return this.vendorSearchResultsForm.get('ssn') };
  get companyCode() { return this.vendorSearchResultsForm.get('companyCode') };
  get vendorName() { return this.vendorSearchResultsForm.get('vendorName') };
  get caseOrderStartDate() { return this.vendorSearchResultsForm.get('caseOrderStartDate') };
  get caseOrderEndDate() { return this.vendorSearchResultsForm.get('caseOrderEndDate') };

  createFormGroup(): void {
    this.vendorSearchResultsForm = this.fb.group<VendorSearchResultsFormModel>({
      caseNumber: this.fb.control('', [Validators.maxLength(19), Validators.pattern(this.regexCaseNumber)]),
      referenceNumber: this.fb.control('', [Validators.maxLength(30), Validators.pattern("^[0-9]*$")]),
      applicantFirstName: this.fb.control('', [Validators.maxLength(50), Validators.pattern(this.regexName)]),
      applicantLastName: this.fb.control('', [Validators.maxLength(50), Validators.pattern(this.regexName)]),
      requesterFirstName: this.fb.control('', [Validators.maxLength(50), Validators.pattern(this.regexName)]),
      requesterLastName: this.fb.control('', [Validators.maxLength(50), Validators.pattern(this.regexName)]),
      ssn: this.fb.control('', [Validators.maxLength(9), Validators.pattern("^[0-9]*$")]),
      companyCode: this.fb.control('', [Validators.maxLength(13), Validators.pattern(/^\d{5}-(\d{7})$/)]),
      vendorName: this.fb.control('', [Validators.maxLength(100), Validators.pattern(this.regexStr)]),
      caseOrderStartDate: this.fb.control('', [Validators.maxLength(10)]),
      caseOrderEndDate: this.fb.control('', [Validators.maxLength(10)])
    }, {
      validators: [this.customValidator.vendorSerachPageDateValidator]
    });
  }

  validate(): boolean {
    if (!this.vendorSearchResultsForm.valid || this.vendorSearchResultsForm.untouched ||
      (
        this.caseNumber.value == "" && this.referenceNumber.value == "" && this.applicantFirstName.value == "" &&
        this.applicantLastName.value == "" && this.requesterFirstName.value == "" && this.requesterLastName.value == "" &&
        this.ssn.value == "" && this.companyCode.value == "" && this.vendorName.value == "" && this.caseOrderStartDate.value == "" &&
        this.caseOrderEndDate.value == ""
      )
    ) {
      return false;
    }
    return true;
  }

  onSearch() {
    if (this.validate()) {
      let vendorResponseDomain = new AFVendorResponseDomain();
      vendorResponseDomain.verifyId = this.userService.getVerifyId();
      if (vendorResponseDomain.verifyId) {
        vendorResponseDomain.caseNumber = this.caseNumber.value;
        vendorResponseDomain.referenceNumber = this.referenceNumber.value;
        vendorResponseDomain.applicantFirstName = this.applicantFirstName.value;
        vendorResponseDomain.applicantLastName = this.applicantLastName.value;
        vendorResponseDomain.requesterFirstName = this.requesterFirstName.value;
        vendorResponseDomain.requesterLastName = this.requesterLastName.value;

        vendorResponseDomain.ssn = this.ssn.value;
        if (this.ssn.value) {
          let salt = bcrypt.genSaltSync();
          vendorResponseDomain.confirmSsn = bcrypt.hashSync(this.ssn.value, salt);
        }

        vendorResponseDomain.companyCostCenter = this.companyCode.value;
        vendorResponseDomain.vendorName = this.vendorName.value;
        vendorResponseDomain.startDateBegin = this.caseOrderStartDate.value;
        vendorResponseDomain.startDateEnd = this.caseOrderEndDate.value;

        this.vendorService.searchVendorResponse(vendorResponseDomain).subscribe({
          next: (searchResult: VendorResultsDomain[]) => {
            this.displayResult = true;
            this.dataSource.data = searchResult;
          },
          error: () => {
            this._snackBar.open("Vendor Search Unsuccessful", "Close", this.config);
          }
        });
      }
    } else {
      this._snackBar.open("The required fields below have not been entered correctly", "Close", this.config);
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onReset() {
    this.vendorSearchResultsForm.reset();
  }

  onTakeAction(caseData: VendorResultsDomain): void {
    this.vendorService.searchVendorTakeActionResponse(caseData.auditIds).subscribe({
      next: (searchResult: VendorTakeActionDomain[]) => {
        this.dialog.open(VendorSearchResultsTakeActionComponent, {
          height: "620px",
          width: "1000px",
          data: {
            applicantName: caseData.applicantName,
            caseNumber: caseData.caseNumber,
            auditIds: caseData.auditIds,
            containerId: caseData.containerId,
            entityId: caseData.entityId,
            tableDataSource: searchResult
          },
          disableClose: true
        });
      },
      error: () => {
        this._snackBar.open("Vendor Data Loading Unsuccessful", "Close", this.config);
      }
    });
  }
}


